import { LoginHero } from '@frontend/assets';
import { match } from '@frontend/duck-tape';
import {
  AppShell,
  ClerkLoading,
  ClerkUnavailable,
  Image,
  SSOButton,
  ScrollableContentPanel,
  Text,
  Title,
  XStack,
  YStack,
  isUserCancellationError,
  showToast,
  useSSOLoadState,
  type OAuthStrategy,
} from '@frontend/web-react';
import { UnauthenticatedAppHeader } from '@frontend/web/components';
import { useAnalytics, useAuthentication, useMcpFlow } from '@frontend/web/hooks';
import { createFileRoute } from '@frontend/web/utils';
import { useEffect } from 'react';

const MCP_ANALYTICS_BASE = {
  entryType: 'mcp',
  flowVariant: 'mcp',
} as const;

const MCPSignup = () => {
  const ssoLoadState = useSSOLoadState();
  const { isLoading, sso } = useAuthentication();
  const { setMcpFlow } = useMcpFlow();
  const { track } = useAnalytics();

  useEffect(() => {
    track({
      data: { ...MCP_ANALYTICS_BASE, hasInviteId: false, hasPromoCode: false },
      event: 'signupLandingViewed',
      topic: 'auth',
    });
  }, [track]);

  const handleSSOSignIn = async (strategy: OAuthStrategy) => {
    const provider = strategy === 'oauth_apple' ? 'apple' : 'google';
    const method = strategy === 'oauth_apple' ? 'sso_apple' : 'sso_google';

    track({
      data: { ...MCP_ANALYTICS_BASE, hasInviteId: false, hasPromoCode: false, method },
      event: 'signupMethodSelected',
      topic: 'auth',
    });
    track({
      data: { ...MCP_ANALYTICS_BASE, provider },
      event: 'ssoStart',
      topic: 'auth',
    });

    try {
      setMcpFlow();
      await sso.startOAuthFlow({
        mode: 'signUp',
        redirectUrl: '/sso-callback?mcpFlow=true',
        redirectUrlComplete: '/sso-callback?mcpFlow=true',
        strategy,
      });
    } catch (error) {
      if (isUserCancellationError(error)) {
        track({
          data: { ...MCP_ANALYTICS_BASE, provider },
          event: 'ssoCancel',
          topic: 'auth',
        });
        return;
      }
      track({
        data: {
          ...MCP_ANALYTICS_BASE,
          errorCategory: 'auth',
          errorCode: 'SSO_START_FAILED',
          errorSource: 'ssoStart',
          provider,
          retriable: true,
        },
        event: 'ssoFail',
        topic: 'auth',
      });
      const providerName = strategy === 'oauth_apple' ? 'Apple' : 'Google';
      showToast({
        title: `Failed to sign up with ${providerName}. Please try again.`,
        variant: 'danger',
      });
    }
  };

  const panelContent = match(ssoLoadState)
    .with({ status: 'loading' }, () => <ClerkLoading />)
    .with({ status: 'failure' }, ({ onReload }) => <ClerkUnavailable onAction={onReload} />)
    .with({ status: 'success' }, () => (
      <YStack className="gap-y-md">
        <Title type="h5">Sign up for Duckbill&apos;s MCP</Title>
        <Text color="secondary">Create your account using one of the options below.</Text>
        <SSOButton
          disabled={!sso.isLoaded || sso.isOAuthRedirecting}
          isLoading={isLoading || sso.activeProvider === 'oauth_google'}
          label="Sign up with Google"
          onClick={() => handleSSOSignIn('oauth_google')}
          provider="google"
        />
        <SSOButton
          disabled={!sso.isLoaded || sso.isOAuthRedirecting}
          isLoading={isLoading || sso.activeProvider === 'oauth_apple'}
          label="Sign up with Apple"
          onClick={() => handleSSOSignIn('oauth_apple')}
          provider="apple"
        />
      </YStack>
    ))
    .exhaustive();

  return (
    <AppShell>
      <UnauthenticatedAppHeader />
      <XStack className="h-screen">
        <ScrollableContentPanel>{panelContent}</ScrollableContentPanel>
        <YStack className="bg-surfaceBrand hidden flex-1 justify-center md:flex">
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <Image className="h-[500px] w-[960px]" source={LoginHero} />
        </YStack>
      </XStack>
    </AppShell>
  );
};

export const Route = createFileRoute('/signup/mcp')({
  component: MCPSignup,
});
